import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Rating,
  Box,
  IconButton,
  Fade,
  CircularProgress,
  Paper,
  Fab,
  TextField
} from '@mui/material';
import { Close as CloseIcon, Feedback as FeedbackIcon, Star as StarIcon, Send as SendIcon, Quiz as QuizIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import { FEEDBACK_ENABLED, PAGE_CONFIGS, COURSE_DETAIL_CONFIG, DEFAULT_CONFIG, getRatingText } from './FeedbackConfig';
import QuestionRenderer from './QuestionRenderer';
import { getTouchpointFromPath } from '../../../utils/helpers';
import apiService from '../../../services/apiService';

interface FeedbackModalProps {
  appLoaded?: boolean;
  isSurveyOpen?: boolean;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ appLoaded = false, isSurveyOpen = false }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [rating, setRating] = useState<number | null>(0);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [hasShownFeedback, setHasShownFeedback] = useState<boolean>(false);
  const [showFab, setShowFab] = useState<boolean>(false);
  const [fabDismissed, setFabDismissed] = useState<boolean>(false);
  const [comment, setComment] = useState<string>('');
  const [questionResponses, setQuestionResponses] = useState<Record<string, any>>({});

  const location = useLocation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const FEEDBACK_STORAGE_KEY = 'app_feedback_submitted';

  const getPageConfig = () => {
    const path = location.pathname;
    if (path.startsWith('/course/courseDetail/')) {
      return COURSE_DETAIL_CONFIG;
    }
    return PAGE_CONFIGS[path] || DEFAULT_CONFIG;
  };

  const pageConfig = getPageConfig();

  useEffect(() => {
    const feedbackSubmitted = localStorage.getItem(FEEDBACK_STORAGE_KEY);

    if (feedbackSubmitted === 'true') {
      setHasShownFeedback(true);
    }

    if (appLoaded && !isSurveyOpen) {
      const timer = setTimeout(() => setShowFab(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [appLoaded, isSurveyOpen]);

  useEffect(() => {
    const feedbackSubmitted = localStorage.getItem(FEEDBACK_STORAGE_KEY);

    if (pageConfig.autoShow && appLoaded && !hasShownFeedback && !open && !isSurveyOpen && feedbackSubmitted !== 'true') {
      const timer = setTimeout(() => setOpen(true), import.meta.env.VITE_SHOW_AUTO_FEEDBACK);
      return () => clearTimeout(timer);
    }
  }, [appLoaded, hasShownFeedback, open, pageConfig.autoShow, isSurveyOpen]);

  const handleQuestionResponse = (questionId: string, value: any) => {
    setQuestionResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleFeedbackSubmitted = async (feedbackData: any) => {
    console.log('feedbackData',feedbackData);
    
    await apiService.post('activities', feedbackData);
  };

  const handleDismissFab = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFabDismissed(true);
    setShowFab(false);
  };

  const isFormValid = () => {
    if (pageConfig.type === 'rating' || pageConfig.type === 'mixed') {
      if (!rating) return false;
    }
    if (pageConfig.questions) {
      const requiredQuestions = pageConfig.questions.filter(q => q.required);
      return requiredQuestions.every(q => questionResponses[q.id]);
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!isFormValid()) return;
    setIsSubmitting(true);
    try {
      const studentId = localStorage.getItem('studentId');

      const feedbackData = {
        reference_id: studentId || '',
        type: 'feedback',
        data: {
          ...(rating !== null && rating !== 0 && { rating }),
          ...(comment && { comment }),
          ...questionResponses,
          touchpoint: getTouchpointFromPath(location.pathname),

        },
      };

      await handleFeedbackSubmitted(feedbackData);

      localStorage.setItem(FEEDBACK_STORAGE_KEY, 'true');

      setHasShownFeedback(true);
      setOpen(false);
      setRating(0);
      setComment('');
      setQuestionResponses({});
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const ratingInfo = getRatingText(rating, theme);

  if (!FEEDBACK_ENABLED || location.pathname === '/login' || isSurveyOpen) return null;

  return (
    <>
      <Fade in={showFab && !fabDismissed} timeout={1000}>
        <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000 }}>
          <Box
            sx={{
              position: 'absolute',
              top: -40,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              zIndex: 1002
            }}
          >
            <Paper
              elevation={4}
              sx={{
                borderRadius: '50%',
                p: 0,
                width: 30,
                height: 30,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isDarkMode ? theme.palette.grey[700] : theme.palette.background.paper,
                border: `2px solid ${theme.palette.grey[500]}`,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.1)',
                  backgroundColor: theme.palette.grey[600],
                  '& .close-icon': {
                    color: '#ffffff'
                  },
                  boxShadow: theme.shadows[8]
                }
              }}
              onClick={handleDismissFab}
            >
              <CloseIcon
                className="close-icon"
                sx={{
                  fontSize: 18,
                  color: isDarkMode ? theme.palette.grey[300] : theme.palette.grey[600],
                  fontWeight: 'bold',
                  transition: 'color 0.3s ease'
                }}
              />
            </Paper>
          </Box>

          <Box
            sx={{
              position: 'absolute',
              right: 76,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: isDarkMode ? theme.palette.grey[800] : theme.palette.background.paper,
              color: theme.palette.text.primary,
              px: 2.5,
              py: 1.5,
              borderRadius: 2,
              fontSize: '0.875rem',
              fontWeight: 500,
              boxShadow: theme.shadows[4],
              border: `1px solid ${theme.palette.divider}`,
              opacity: 0,
              animation: 'showLongTooltip 20s ease-in-out 2s forwards',
              whiteSpace: 'nowrap',
              '&::after': {
                content: '""',
                position: 'absolute',
                left: '100%',
                top: '50%',
                transform: 'translateY(-50%)',
                border: `6px solid transparent`,
                borderLeftColor: isDarkMode ? theme.palette.grey[800] : theme.palette.background.paper
              },
              '@keyframes showLongTooltip': {
                '0%': { opacity: 0, transform: 'translateY(-50%) translateX(20px)' },
                '5%': { opacity: 1, transform: 'translateY(-50%) translateX(0)' },
                '85%': { opacity: 1, transform: 'translateY(-50%) translateX(0)' },
                '100%': { opacity: 0, transform: 'translateY(-50%) translateX(-20px)' }
              }
            }}
          >
            💬 Share your feedback
          </Box>

          <Fab
            onClick={() => setOpen(true)}
            sx={{
              background: isDarkMode
                ? theme.palette.grey[700]
                : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: isDarkMode ? theme.palette.primary.light : '#ffffff',
              border: 'none',
              boxShadow: isDarkMode ? theme.shadows[3] : theme.shadows[6],
              '&:hover': {
                background: isDarkMode
                  ? theme.palette.grey[600]
                  : `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                transform: 'scale(1.1)',
                boxShadow: isDarkMode ? theme.shadows[6] : theme.shadows[8]
              },
              transition: 'all 0.3s ease',
              width: 60,
              height: 60,
              position: 'relative'
            }}
          >
            <Box sx={{ position: 'relative' }}>
              <FeedbackIcon sx={{ color: isDarkMode ? theme.palette.primary.light : '#ffffff', fontSize: 26 }} />
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.error.main,
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  border: `2px solid ${isDarkMode ? theme.palette.grey[700] : '#ffffff'}`,
                  animation: 'gentlePulse 3s infinite',
                  '@keyframes gentlePulse': {
                    '0%': { transform: 'scale(1)', opacity: 1 },
                    '50%': { transform: 'scale(1.1)', opacity: 0.8 },
                    '100%': { transform: 'scale(1)', opacity: 1 }
                  }
                }}
              />
            </Box>
          </Fab>
        </Box>
      </Fade>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxWidth: '520px',
            maxHeight: '90vh',
            background: theme.palette.background.paper,
            border: isDarkMode ? `1px solid ${theme.palette.grey[700]}` : `1px solid ${theme.palette.divider}`,
            boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.8)' : theme.shadows[8]
          }
        }}
        BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(3px)' } }}
      >
        <Box
          sx={{
            background: isDarkMode
              ? `linear-gradient(135deg, ${theme.palette.grey[800]}, ${theme.palette.grey[900]})`
              : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            color: '#ffffff',
            p: 3,
            position: 'relative',
            borderBottom: isDarkMode ? `1px solid ${theme.palette.grey[700]}` : 'none'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: isDarkMode ? `rgba(${theme.palette.primary.main.replace('#', '')}, 0.2)` : 'rgba(255, 255, 255, 0.2)',
                  border: isDarkMode ? `1px solid ${theme.palette.primary.main}` : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {pageConfig.type === 'questions' ? <QuizIcon sx={{ color: '#ffffff' }} /> : <FeedbackIcon sx={{ color: '#ffffff' }} />}
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: '#ffffff' }}>
                  {pageConfig.title}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, color: '#ffffff' }}>
                  {pageConfig.subtitle}
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={() => setOpen(false)}
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <DialogContent sx={{ p: 3, maxHeight: 'calc(90vh - 200px)', overflowY: 'auto' }}>
          {(pageConfig.type === 'rating' || pageConfig.type === 'mixed') && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                background: isDarkMode ? theme.palette.grey[800] : theme.palette.grey[50]
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <StarIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  {pageConfig.ratingLabel} <span style={{ color: theme.palette.error.main }}>*</span>
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Rating
                  value={rating}
                  onChange={(_, newValue) => setRating(newValue)}
                  size="large"
                  sx={{
                    '& .MuiRating-iconFilled': { color: theme.palette.primary.main },
                    '& .MuiRating-iconHover': { color: theme.palette.primary.dark },
                    '& .MuiRating-iconEmpty': { color: isDarkMode ? theme.palette.grey[600] : theme.palette.grey[400] }
                  }}
                />
                {ratingInfo && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: ratingInfo.color }}>
                      {ratingInfo.text}
                    </Typography>
                    <span>{ratingInfo.emoji}</span>
                  </Box>
                )}
              </Box>
            </Paper>
          )}
          {pageConfig.questions && (
            <Box sx={{ mb: 3 }}>
              {pageConfig.questions.map(question => (
                <QuestionRenderer
                  key={question.id}
                  question={question}
                  questionResponses={questionResponses}
                  handleQuestionResponse={handleQuestionResponse}
                />
              ))}
            </Box>
          )}
          {(pageConfig.type === 'rating' || pageConfig.type === 'mixed') && pageConfig.placeholder && (
            <Box sx={{ mb: 3 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  background: isDarkMode ? theme.palette.grey[800] : theme.palette.grey[50]
                }}
              >
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}>
                  💬 Additional Comments 
                </Typography>
                <TextField
                  multiline
                  rows={3}
                  fullWidth
                  placeholder={pageConfig.placeholder}
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: theme.palette.background.paper,
                      '& input, & textarea': { color: theme.palette.text.primary },
                      '& .MuiInputBase-input::placeholder': { color: theme.palette.text.secondary, opacity: 1 },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main }
                    }
                  }}
                />
              </Paper>
            </Box>
          )}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: isDarkMode ? `${theme.palette.info.dark}20` : `${theme.palette.info.light}15`,
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              🔒 Your feedback is anonymous and helps us improve
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, justifyContent: 'flex-end', borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!isFormValid() || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : <SendIcon />}
            sx={{
              minWidth: 150,
              height: 40,
              fontWeight: 600,
              fontSize: '0.95rem',
              borderRadius: 2,
              boxShadow: theme.shadows[3],
              '&:hover': {
                boxShadow: theme.shadows[4],
                transform: 'translateY(-1px)'
              },
              '&:disabled': {
                '& .MuiCircularProgress-root': {
                  color: theme.palette.action.disabled
                }
              },
              transition: 'all 0.2s ease'
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FeedbackModal;
